import type { Ball, Vec2 } from './ball.js'
import { resolveCircleCollision } from './collision.js'
import { HOOP_GAP, UPRIGHT_RADIUS } from './constants.js'

export type HoopCollisionResult = 'none' | 'left' | 'right' | 'scored'

/**
 * A croquet hoop: two cylindrical uprights with a gap between them.
 *
 * In top-down 2D:
 *   - Each upright is a circle (radius = UPRIGHT_RADIUS)
 *   - The hoop faces a direction: the "through" axis is perpendicular to the upright pair
 *   - Scoring: ball crosses the centreline between the two uprights while inside the gap
 *
 * Coordinate convention: uprights are placed left/right along the local x-axis.
 * The "through" direction is along the local y-axis (ball must approach and cross along y).
 *
 * For Bamford, all hoops face "up the court" (ball shoots from bottom to top).
 */
export class Hoop {
  /** Centre of the hoop in world coordinates */
  centre: Vec2
  /** Angle the hoop faces (radians). 0 = ball shoots in +x direction. */
  facing: number

  readonly gap: number
  readonly uprightRadius: number

  // Computed upright positions
  left: Vec2
  right: Vec2

  constructor(cx: number, cy: number, facing = -Math.PI / 2, gap = HOOP_GAP, uprightRadius = UPRIGHT_RADIUS) {
    this.centre = { x: cx, y: cy }
    this.facing = facing
    this.gap = gap
    this.uprightRadius = uprightRadius
    this.left = { x: 0, y: 0 }
    this.right = { x: 0, y: 0 }
    this._computeUprights()
  }

  private _computeUprights(): void {
    // Perpendicular to facing direction
    const perpX = Math.cos(this.facing + Math.PI / 2)
    const perpY = Math.sin(this.facing + Math.PI / 2)
    const half = (this.gap / 2) + this.uprightRadius
    this.left = {
      x: this.centre.x - perpX * half,
      y: this.centre.y - perpY * half,
    }
    this.right = {
      x: this.centre.x + perpX * half,
      y: this.centre.y + perpY * half,
    }
  }

  /**
   * Check and resolve any collision between the ball and this hoop's uprights.
   * Returns whether the left, right, or neither upright was hit.
   * Does NOT check scoring (use checkScoring separately).
   */
  resolveCollisions(ball: Ball): HoopCollisionResult {
    const hitLeft = resolveCircleCollision(ball, { pos: this.left, radius: this.uprightRadius })
    const hitRight = resolveCircleCollision(ball, { pos: this.right, radius: this.uprightRadius })
    if (hitLeft) return 'left'
    if (hitRight) return 'right'
    return 'none'
  }

  /**
   * Check if the ball has scored by passing through this hoop.
   *
   * Method: project ball position onto the hoop's facing axis.
   * The ball scores if:
   *   1. It has crossed the "through line" (perpendicular to facing, through centre)
   *   2. Its lateral position is within the gap (not hitting an upright)
   *
   * @param prevPos  Ball position from the previous frame
   * @param currPos  Ball position this frame
   * @returns true if ball passed through the hoop this frame
   */
  checkScoring(prevPos: Vec2, currPos: Vec2): boolean {
    // Facing direction unit vector (the "through" axis)
    const fx = Math.cos(this.facing)
    const fy = Math.sin(this.facing)

    // Project positions onto facing axis (signed distance from hoop centre)
    const prevProj = (prevPos.x - this.centre.x) * fx + (prevPos.y - this.centre.y) * fy
    const currProj = (currPos.x - this.centre.x) * fx + (currPos.y - this.centre.y) * fy

    // Ball must cross the through-line (sign change)
    const crossed = (prevProj < 0 && currProj >= 0) || (prevProj > 0 && currProj <= 0)
    if (!crossed) return false

    // Perpendicular axis (along uprights)
    const px = -fy
    const py = fx
    const lateralDist = Math.abs(
      (currPos.x - this.centre.x) * px + (currPos.y - this.centre.y) * py
    )

    // Must be within the clear gap (not in contact with an upright)
    return lateralDist < this.gap / 2
  }
}
